import { X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AgentApplyForm, type AgentApplyContext } from './AgentApplyForm';

/* ------------------------------------------------------------------ */
/*  AgentApplyModal                                                     *
 *  Full-screen page-takeover overlay for the agent application form.   *
 *  Mirrors the InquiryModal pattern (portal to body, deep-teal bg, X   *
 *  close button top-right, Escape-to-close). Mounts AgentApplyForm     *
 *  inside the centered container; auto-closes a few seconds after a    *
 *  successful submission via the form's onSuccess callback.            */
/* ------------------------------------------------------------------ */

interface AgentApplyModalProps {
    open: boolean;
    onClose: () => void;
    context?: AgentApplyContext;
}

export function AgentApplyModal({ open, onClose, context }: AgentApplyModalProps) {
    // Escape to close
    useEffect(() => {
        if (!open) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open, onClose]);

    // Lock background scroll while open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto" style={{ backgroundColor: '#0b2222' }}>
            <button
                type="button"
                onClick={onClose}
                aria-label="Close application form"
                className="fixed top-6 right-6 z-[10000] flex h-10 w-10 cursor-pointer items-center justify-center rounded-full transition-colors"
                style={{ backgroundColor: 'rgba(245, 245, 245, 0.08)', color: 'rgba(245, 245, 245, 0.6)' }}
            >
                <X size={20} />
            </button>

            <div className="flex min-h-full items-start justify-center px-6 pt-20 pb-12 md:items-center md:pt-16 md:pb-16">
                <AgentApplyForm context={context} />
            </div>
        </div>,
        document.body,
    );
}
